/**
 * WHAT:
 *   PDF upload area component with drag & drop functionality.
 *
 * WHY:
 *   Centralizes file upload UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - TypeScript types (PdfUploadAreaProps)
 *   - Browser File API
 *
 * RISKS:
 *   - NOTE: Drag & drop may not work in all browsers
 *   - NOTE: File input reset via key prop may cause issues
 *   - WARN: Large files may cause UI blocking during upload
 */

import type { PdfUploadAreaProperties } from "../types";

/**
 * WHAT:
 *   Component displaying drag & drop area for PDF file upload.
 *
 * WHY:
 *   Encapsulates file upload UI logic.
 *
 * @param props - Component props
 *
 * @returns {JSX.Element} The PdfUploadArea component
 *
 */
export const PdfUploadArea = (props: PdfUploadAreaProperties) => {
  const {
    fileInputKey,
    isDragOver,
    isProcessing,
    onDragLeave,
    onDragOver,
    onDrop,
    onFileSelect,
    onProcessPdf,
    onProcessPdfRaw,
    onReset,
    uploadedFile,
  } = props;
  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
        isDragOver
          ? "border-tracq-bright-blue bg-tracq-bright-blue/10"
          : "border-slate-500 hover:border-slate-400"
      }`}
      onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
        (onDragLeave as (e: React.DragEvent) => void)(e);
      }}
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
        (onDragOver as (e: React.DragEvent) => void)(e);
      }}
      onDrop={(e: React.DragEvent<HTMLDivElement>) => {
        (onDrop as (e: React.DragEvent) => void)(e);
      }}
    >
      {uploadedFile ? (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-3">
            <svg className="w-8 h-8 text-tracq-bright-blue" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2Z" />
            </svg>
            <div className="text-left">
              <div className="text-white font-medium">
                {uploadedFile instanceof File ? uploadedFile.name : ""}
              </div>
              <div className="text-slate-400 text-sm">
                {uploadedFile instanceof File
                  ? (uploadedFile.size / 1024 / 1024).toFixed(2)
                  : "0.00"}{" "}
                MB
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              className="px-4 py-2 bg-tracq-bright-blue/20 text-tracq-bright-blue rounded-lg text-sm font-medium hover:bg-tracq-bright-blue/30 transition disabled:opacity-50"
              disabled={isProcessing}
              onClick={() => {
                // Reason: Props type inference issue, explicit type assertion needed

                (onProcessPdf as unknown as () => void)();
              }}
            >
              {isProcessing ? "Processing…" : "Parse PDF"}
            </button>
            <button
              className="px-4 py-2 bg-orange-600/20 text-orange-400 rounded-lg text-sm font-medium hover:bg-orange-600/30 transition disabled:opacity-50"
              disabled={isProcessing}
              onClick={() => {
                // Reason: Props type inference issue, explicit type assertion needed

                (onProcessPdfRaw as unknown as () => void)();
              }}
            >
              {isProcessing ? "Processing…" : "Raw Data Debug"}
            </button>
            <button
              className="px-4 py-2 bg-slate-600/30 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-600/50 transition"
              onClick={() => {
                (onReset as () => void)();
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <svg className="w-12 h-12 text-slate-400 mx-auto" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2Z" />
          </svg>
          <div>
            <div className="text-white font-medium mb-2">Drop PDF files here</div>
            <div className="text-slate-400 text-sm mb-4">or choose upload method</div>
            <div className="flex gap-3 justify-center">
              <input
                accept=".pdf"
                className="hidden"
                id="pdf-upload"
                key={fileInputKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const target = e.target;
                  if (target instanceof HTMLInputElement && target.files?.[0]) {
                    const file: File = target.files[0];
                    (onFileSelect as (file: File) => void)(file);
                  }
                }}
                type="file"
              />
              <label
                className="px-4 py-2 bg-tracq-bright-blue/20 text-tracq-bright-blue rounded-lg text-sm font-medium hover:bg-tracq-bright-blue/30 cursor-pointer inline-block"
                htmlFor="pdf-upload"
              >
                Choose PDF
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
